import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cloneDeep } from 'lodash';

import { usePopup } from '../../context';
import { SelectWordModePopup } from '../../components/Popups';
import { CreateMemoryGameSelectWordsTemplate } from '../../components/Templates';
import { WordSelectionMode } from '../../constants/MemoryGames/wordSelectionMode';
import { QuickCreateGameMode } from '../../models/DraftStore';
import { TextOrderingUnitType } from '../../models/GameStore';
import { useDraftStore } from '../../stores/DraftStore';

interface State {
  createType: QuickCreateGameMode | undefined;
  modeCells: WordSelectionMode[];
  showRequiredWarning: boolean;
}

const CreateMemoryGameSelectWordsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const [state, setState] = useState<State>({
    createType: undefined,
    modeCells: [],
    showRequiredWarning: false,
  });

  const { mode, selectWords } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    const _selectWords = store.getSelectWords();
    return {
      mode: _mode,
      selectWords: _selectWords,
    };
  });

  useEffect(() => {
    if (selectWords.length === 0) {
      const modeCells = Array.from(selectWords).map(
        () => WordSelectionMode.HandpickKeywordsSuggestions,
      );
      setState((s) => ({ ...s, modeCells: modeCells }));
    }
  }, [selectWords]);

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  useEffect(() => {
    const handlePageShow = () => {
      navigate('/create/memory-game/generated-games', { replace: true });
    };

    window.addEventListener('popstate', handlePageShow);

    return () => {
      window.removeEventListener('popstate', handlePageShow);
    };
  }, [navigate]);

  const onSelectMode = useCallback(
    (_mode: WordSelectionMode, index: number) => {
      const _modeCells = cloneDeep(state.modeCells);
      _modeCells[index] = _mode;
      setState((s) => ({ ...s, modeCells: _modeCells }));
      useDraftStore.getState().changeSelectWordsMode(index, _mode);
    },
    [state.modeCells],
  );

  const onClickMode = useCallback(
    (index: number) => {
      showPopup(
        <SelectWordModePopup
          selectedMode={
            state.modeCells[index] ??
            WordSelectionMode.HandpickKeywordsSuggestions
          }
          onPrimary={hidePopup}
          onSelectMode={(_mode) => {
            onSelectMode(_mode, index);
            hidePopup();
          }}
        />,
      );
    },
    [hidePopup, onSelectMode, showPopup, state.modeCells],
  );

  const onUnitClick = useCallback(
    (cellIndex: number, unitIndex: number) => {
      useDraftStore.getState().changeSelectWordsUnitType(cellIndex, unitIndex);
      const originalMode = state.modeCells[cellIndex];
      if (originalMode === WordSelectionMode.SelectAllWords) {
        const _modeCells = cloneDeep(state.modeCells);
        _modeCells[cellIndex] = WordSelectionMode.HandpickKeywords;
        setState((s) => ({ ...s, modeCells: _modeCells }));
      }
    },
    [state.modeCells],
  );

  const selectWordCells = useMemo(() => {
    return selectWords.map((word, index) => {
      return {
        mode:
          state.modeCells[index] ??
          WordSelectionMode.HandpickKeywordsSuggestions,
        units: word,
      };
    });
  }, [selectWords, state.modeCells]);

  const onClickBack = useCallback(() => {
    navigate('/create/memory-game/generated-games', { replace: true });
  }, [navigate]);

  const onClickNext = useCallback(() => {
    const showRequiredWarning = selectWords.some((word) =>
      word.every((unit) => unit.type === TextOrderingUnitType.Hint),
    );
    if (showRequiredWarning) {
      setState((s) => ({ ...s, showRequiredWarning: true }));
    } else {
      setState((s) => ({ ...s, showRequiredWarning: false }));
      useDraftStore.getState().convertDraftsToProducts();
      navigate('/create/memory-game/select-games', { replace: true });
    }
  }, [navigate, selectWords]);

  return (
    <CreateMemoryGameSelectWordsTemplate
      type={state.createType}
      selectWordCells={selectWordCells}
      showRequiredWarning={state.showRequiredWarning}
      onClickMode={onClickMode}
      onUnitClick={onUnitClick}
      onClickBack={onClickBack}
      onClickNext={onClickNext}
    />
  );
};
export default CreateMemoryGameSelectWordsScreen;
